import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'hooks/useStore';
import { OOCTabType, FCColorType, OOCForecastAdjustment } from '../oocUpdated.type';

export const ROWS_PER_PAGE = 6;

export const TAB_LABELS: Record<OOCTabType, string> = {
  MAS: 'MAS',
  NC: 'NC',
  ProvMAS: 'Prov MAS',
  ProvNC: 'Prov NC',
};

export const TAB_ORDER: OOCTabType[] = ['MAS', 'NC', 'ProvMAS', 'ProvNC'];

export const getDetailFcClass = (colorType: FCColorType, styles: Record<string, string>): string => {
  switch (colorType) {
    case 'new':
      return styles.detailFcNew;
    case 'modified':
      return styles.detailFcModified;
    case 'removed':
      return styles.detailFcRemoved;
    default:
      return styles.detailFcUnchanged;
  }
};

export const useOOCHistoryLogic = () => {
  const { OOCStoreUpdated: OOCStore } = useStore();
  const { historyData: rawHistoryData, isHistoryLoading, fetchHistory, navigateToDashboard } = OOCStore;

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const [first, setFirst] = React.useState(0);

  // Local search state — client-side filtering, no API call triggered
  const [searchInput, setSearchInput] = useState('');

  const historyData = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return rawHistoryData;
    return rawHistoryData.filter((item) =>
      item.oocId.toLowerCase().includes(q) ||
      item.submittedBy.toLowerCase().includes(q) ||
      item.reason.toLowerCase().includes(q) ||
      item.integratedIntoCycle.toLowerCase().includes(q) ||
      (item.searchText?.includes(q) ?? false),
    );
  }, [rawHistoryData, searchInput]);

  const recentIntegrations = useMemo(() => historyData.slice(0, 3), [historyData]);
  const integratedCount = recentIntegrations.length;

  const visibleCount = Math.min(ROWS_PER_PAGE, historyData.length - first);

  const handleClearSearch = useCallback(() => {
    setFirst(0);
    setSearchInput('');
  }, []);

  const handleRowClick = (oocId: string) => {
    OOCStore.fetchHistoryDetail(oocId);
  };

  return {
    OOCStore,
    historyData,
    isHistoryLoading,
    navigateToDashboard,
    recentIntegrations,
    integratedCount,
    first,
    setFirst,
    visibleCount,
    handleRowClick,
    searchInput,
    setSearchInput,
    handleClearSearch,
  };
};

export const useHistoryDetailLogic = () => {
  const { OOCStoreUpdated: OOCStore } = useStore();
  const { historyDetailSubmission, isHistoryDetailLoading, closeHistoryDetail } = OOCStore;

  const [activeTab, setActiveTab] = useState<OOCTabType>('MAS');

  const visible = historyDetailSubmission !== null || isHistoryDetailLoading;

  const tabGroups = useMemo(() => {
    if (!historyDetailSubmission) return {} as Partial<Record<OOCTabType, OOCForecastAdjustment[]>>;
    const groups: Partial<Record<OOCTabType, OOCForecastAdjustment[]>> = {};
    TAB_ORDER.forEach((tab) => {
      const filtered = historyDetailSubmission.adjustments.filter((a) => a.tab === tab);
      if (filtered.length > 0) groups[tab] = filtered;
    });
    return groups;
  }, [historyDetailSubmission]);

  const availableTabs = Object.keys(tabGroups) as OOCTabType[];
  const currentTab = availableTabs.includes(activeTab) ? activeTab : availableTabs[0] ?? 'MAS';
  const currentAdjs = tabGroups[currentTab] ?? [];
  const periods = currentAdjs[0]?.forecasts.map((f) => f.period) ?? [];
  const showProbability = currentTab === 'ProvMAS';

  return {
    historyDetailSubmission,
    isHistoryDetailLoading,
    closeHistoryDetail,
    visible,
    activeTab,
    setActiveTab,
    tabGroups,
    availableTabs,
    currentTab,
    currentAdjs,
    periods,
    showProbability,
  };
};
